/**
 * Bridges fish-web's legacy activity-mode layer system to the ai-toolkit's
 * generic `LayersAdapter` contract, powering the `mapGetLayers`/
 * `mapToggleLayers` AI tools.
 *
 * The allowlist mirrors `layerService.getFilteredModeLayers()`'s filter —
 * a label, a thumbnail image, and a passing feature gate — so the AI can
 * never see or toggle a layer the user can't.
 */
import type {
  LayersAdapter,
  ToolboxLayer,
} from '@onxmaps/ai-toolkit/toolbox/definitions/mapSetLayers'
import { MODES } from '@/fish/lib/enums'
import { FISH_ACTIVITY_MODES } from '@/fish/lib/layers/activity-modes'
import layerService from '@/services/layer-service'
import { isActive } from '@/services/feature-flag-service'
import { getItem, saveItem } from '@/plugins/storage'
import { setWindLayer } from '@/fish/components/ui/wind/utils'
import { WIND_LAYER_INFO } from '@/fish/components/map-controls/utils'
import type { XWeatherContext } from '@/fish/providers/x-weather'
import type { FeatureFlagKey } from '@/lib/feature-flags'

interface ActivityModeLayer {
  id: string
  label?: string
  byline?: string
  layerGateKey?: FeatureFlagKey
  img?: string
}

const getActivityModeLayers = (): ActivityModeLayer[] =>
  FISH_ACTIVITY_MODES[MODES.FRESHWATER].layers as ActivityModeLayer[]

const isAllowlisted = (layer: ActivityModeLayer): boolean =>
  !!layer.label &&
  !!layer.img &&
  (!layer.layerGateKey || !!isActive(layer.layerGateKey))

const getAllowlistedLayers = (): ActivityModeLayer[] =>
  getActivityModeLayers().filter(isAllowlisted)

const toToolboxLayer = (layer: ActivityModeLayer): ToolboxLayer | undefined => {
  const runtime = layerService.getLayer(layer.id) as
    | { active?: boolean; hasAccess?: boolean }
    | undefined
  if (!runtime) {
    return undefined
  }
  return {
    id: layer.id,
    name: layer.label,
    description: layer.byline,
    isActive: !!runtime.active,
    hasAccess: runtime.hasAccess ?? true,
    thumbnailUrl: layer.img,
  }
}

const WIND_STORAGE_KEY = 'wind-forecast-enabled'

/**
 * What the adapter needs to expose the Wind Forecast. Wind is not a
 * `layerService` layer: it's an xWeather map layer driven by a stored flag and
 * the `animated_wind` access gate (see `wind-layer-toggle.vue`), and both the
 * controller and the gate live in the map page's component scope, so the host
 * hands them in rather than the adapter reaching for them.
 */
export interface WindLayerControl {
  xWeather: XWeatherContext
  /** Live read of the `animated_wind` access gate. */
  hasAccess: () => boolean
}

const toWindToolboxLayer = (wind: WindLayerControl): ToolboxLayer => ({
  id: WIND_LAYER_INFO.id,
  name: WIND_LAYER_INFO.label,
  description: WIND_LAYER_INFO.byline,
  isActive: !!getItem<boolean>(WIND_STORAGE_KEY),
  hasAccess: wind.hasAccess(),
  thumbnailUrl: WIND_LAYER_INFO.img,
})

/**
 * Creates the `LayersAdapter` fish-web's `mapGetLayers`/`mapToggleLayers`
 * tools drive. Injected into the portable `lib/ai` module from the host.
 *
 * Pass `wind` to also expose the Wind Forecast; without it the layer is
 * simply absent, so the model never sees a layer it can't drive.
 */
export function createLayersAdapter(
  options: { wind?: WindLayerControl } = {}
): LayersAdapter {
  const { wind } = options
  return {
    ready: () => layerService.layersReadyPromise,
    listToggleableLayers: () => [
      ...getAllowlistedLayers().flatMap((layer) => {
        const toolboxLayer = toToolboxLayer(layer)
        return toolboxLayer ? [toolboxLayer] : []
      }),
      ...(wind ? [toWindToolboxLayer(wind)] : []),
    ],
    getLayer: (id) => {
      if (wind && id === WIND_LAYER_INFO.id) {
        return toWindToolboxLayer(wind)
      }
      const layer = getAllowlistedLayers().find((l) => l.id === id)
      return layer ? toToolboxLayer(layer) : undefined
    },
    setLayerActive: (id, active) => {
      if (wind && id === WIND_LAYER_INFO.id) {
        if (
          !wind.hasAccess() ||
          !!getItem<boolean>(WIND_STORAGE_KEY) === active
        ) {
          return
        }
        saveItem(WIND_STORAGE_KEY, active)
        setWindLayer(active, wind.xWeather)
        return
      }
      const layer = getAllowlistedLayers().find((l) => l.id === id)
      const runtime = layer
        ? (layerService.getLayer(id) as
            | { active?: boolean; hasAccess?: boolean }
            | undefined)
        : undefined
      if (
        !runtime ||
        (runtime.hasAccess ?? true) === false ||
        !!runtime.active === active
      ) {
        return
      }
      layerService.toggleLayer(id)
    },
  }
}
